import { logger } from "@/common/logger";
import { ActivityAsideContent } from "@/components/ActivityAsideContent";
import {
  ActivityOverviewEmptySection,
  ActivityOverviewSection,
} from "@/components/ActivityOverviewSection";
import { AudienceGroups } from "@/components/AudienceGroups";
import EditMarkdownSection from "@/components/EditMarkdownSection";
import Markdown from "@/components/Markdown";
import { Rewards } from "@/components/Rewards";
import { Timeline } from "@/components/Timeline";
import { AsideRoot, AsideSection } from "@/kit/ui/aside";
import { BASE_TOC_TITLES } from "@/utils/activity";
import { extractMarkdownTitles, TOCItem } from "@/utils/generateToc";
import { useMemo } from "react";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { Environment, graphql, useFragment, useMutation } from "react-relay";
import { CompetitionOverviewPageDescriptionsFragment$key } from "./__generated__/CompetitionOverviewPageDescriptionsFragment.graphql";
import { CompetitionOverviewPageLegacyDescriptionFragment$key } from "./__generated__/CompetitionOverviewPageLegacyDescriptionFragment.graphql";
import { CompetitionOverviewPageUpdateCompetitionMutation as CompetitionOverviewPageUpdateCompetitionMutationType } from "./__generated__/CompetitionOverviewPageUpdateCompetitionMutation.graphql";
import { LoaderArgs } from "@/utils/route";
import { CompetitionOverviewPageQuery as CompetitionOverviewPageQueryType } from "./__generated__/CompetitionOverviewPageQuery.graphql";
import { preload, usePreloaded } from "@/utils/relay";
import { ActivityNotFound } from "@/components/ActivityNotFound";

const TITLES = Object.freeze([
  ...BASE_TOC_TITLES,
] as const) satisfies ReadonlyArray<TOCItem>;
const [ABOUT, TIMELINE, PURPOSE, AUDIENCE, PRIZES, GETTING_STARTED] = TITLES;
const CompetitionOverviewPageUpdateCompetitionMutation = graphql`
  mutation CompetitionOverviewPageUpdateCompetitionMutation(
    $id: ID!
    $input: UpdateCompetitionInput!
  ) {
    updateCompetition(id: $id, input: $input) {
      node {
        id
        description
      }
    }
  }
`;
const CompetitionOverviewPageLegacyDescriptionFragment = graphql`
  fragment CompetitionOverviewPageLegacyDescriptionFragment on Competition {
    id
    description
    timeline {
      ...TimelineFragment
    }
    host {
      ...HostCardFragment
    }
    ...TagListFragment
  }
`;
const CompetitionOverviewPageDescriptionsFragment = graphql`
  fragment CompetitionOverviewPageDescriptionsFragment on Competition {
    about
    purpose
    gettingStarted
    timeline {
      ...TimelineFragment
    }
    audienceGroups {
      totalCount
      ...AudienceGroupsFragment
    }
    rewardCount: rewards {
      totalCount
    }
    host {
      ...HostCardFragment
    }
    ...RewardsFragment
    ...TagListFragment
  }
`;
export default function CompetitionOverviewPage() {
  const { query } = useCompetitionOverviewPageQuery();
  if (!query.competitionBySlug) {
    return <ActivityNotFound kind="competition" />;
  }
  const competition = query.competitionBySlug;
  return competition.useLegacyDescription ? (
    <LegacyDescription
      competition={competition}
      viewerCanUpdate={competition.viewerCanUpdate}
    />
  ) : (
    <DescriptionFields competition={competition} />
  );
}
interface LegacyDescriptionProps {
  competition: CompetitionOverviewPageLegacyDescriptionFragment$key;
  viewerCanUpdate: boolean;
}
function LegacyDescription(props: LegacyDescriptionProps) {
  const intl = useIntl();
  const [commitMutation, isMutationInFlight] =
    useMutation<CompetitionOverviewPageUpdateCompetitionMutationType>(
      CompetitionOverviewPageUpdateCompetitionMutation,
    );
  const { id, description, timeline, host, ...competition } = useFragment(
    CompetitionOverviewPageLegacyDescriptionFragment,
    props.competition,
  );
  const titles = useMemo(
    () => extractMarkdownTitles(description ?? ""),
    [description],
  );
  return (
    <AsideRoot>
      <ActivityAsideContent
        titles={titles}
        host={host}
        taggable={competition}
        activity="COMPETITION"
      />
      <AsideSection>
        <div className="space-y-8">
          <ActivityOverviewSection
            title={<FormattedMessage defaultMessage="Timeline" />}
          >
            {timeline ? (
              <Timeline timeline={timeline} />
            ) : (
              <ActivityOverviewEmptySection field="timeline" />
            )}
          </ActivityOverviewSection>
          <ActivityOverviewSection
            title={<FormattedMessage defaultMessage="Description" />}
          >
            <EditMarkdownSection
              id={id}
              canEdit={props.viewerCanUpdate}
              disabled={isMutationInFlight}
              content={description ?? ""}
              fallback={<ActivityOverviewEmptySection field="description" />}
              onSubmit={({ content }) =>
                new Promise<void>((resolve, reject) =>
                  commitMutation({
                    variables: {
                      id,
                      input: { description: content || null },
                    },
                    onCompleted: () => resolve(),
                    onError: (error) => {
                      logger.error(error);
                      reject(
                        intl.formatMessage({
                          defaultMessage:
                            "An error occurred while saving the description. Please, try again later.",
                        }),
                      );
                    },
                  }),
                )
              }
              canUploadFiles
            />
          </ActivityOverviewSection>
        </div>
      </AsideSection>
    </AsideRoot>
  );
}
interface DescriptionFieldsProps {
  competition: CompetitionOverviewPageDescriptionsFragment$key;
}
function DescriptionFields(props: DescriptionFieldsProps) {
  const {
    about,
    purpose,
    gettingStarted,
    audienceGroups,
    timeline,
    rewardCount,
    host,
    ...competition
  } = useFragment(
    CompetitionOverviewPageDescriptionsFragment,
    props.competition,
  );
  return (
    <AsideRoot>
      <ActivityAsideContent
        titles={TITLES}
        host={host}
        taggable={competition}
        activity="COMPETITION"
      />
      <AsideSection>
        <div className="space-y-8">
          <ActivityOverviewSection id={ABOUT.id}>
            {about ? (
              <Markdown>{about}</Markdown>
            ) : (
              <ActivityOverviewEmptySection field="about" />
            )}
          </ActivityOverviewSection>

          <ActivityOverviewSection
            id={TIMELINE.id}
            title={<FormattedMessage defaultMessage="Timeline" />}
          >
            {timeline ? (
              <Timeline timeline={timeline} />
            ) : (
              <ActivityOverviewEmptySection field="timeline" />
            )}
          </ActivityOverviewSection>

          <ActivityOverviewSection
            id={PURPOSE.id}
            title={<FormattedMessage defaultMessage="Purpose" />}
          >
            {purpose ? (
              <Markdown>{purpose}</Markdown>
            ) : (
              <ActivityOverviewEmptySection field="purpose" />
            )}
          </ActivityOverviewSection>

          <ActivityOverviewSection
            id={AUDIENCE.id}
            title={<FormattedMessage defaultMessage="Participants" />}
          >
            {audienceGroups.totalCount === 0 ? (
              <ActivityOverviewEmptySection field="audience" />
            ) : (
              <AudienceGroups audience={audienceGroups} />
            )}
          </ActivityOverviewSection>

          <ActivityOverviewSection
            id={PRIZES.id}
            title={<FormattedMessage defaultMessage="Prizes" />}
          >
            {rewardCount.totalCount === 0 ? (
              <ActivityOverviewEmptySection field="prizes" />
            ) : (
              <Rewards activity={competition} />
            )}
          </ActivityOverviewSection>

          <ActivityOverviewSection
            id={GETTING_STARTED.id}
            title={<FormattedMessage defaultMessage="Getting started" />}
          >
            {gettingStarted ? (
              <Markdown>{gettingStarted}</Markdown>
            ) : (
              <ActivityOverviewEmptySection field="getting started" />
            )}
          </ActivityOverviewSection>
        </div>
      </AsideSection>
    </AsideRoot>
  );
}
const CompetitionOverviewPageQuery = graphql`
  query CompetitionOverviewPageQuery($slug: String!) {
    competitionBySlug(slug: $slug) {
      id
      useLegacyDescription
      viewerCanUpdate: viewerCan(action: UPDATE_COMPETITION)
      ...CompetitionOverviewPageLegacyDescriptionFragment
      ...CompetitionOverviewPageDescriptionsFragment
    }
  }
`;
export const loader = (environment: Environment) => {
  return ({ params: { track: slug } }: LoaderArgs) => {
    if (!slug) {
      throw new Response("No slug", { status: 500 });
    }
    return preload<CompetitionOverviewPageQueryType>(
      environment,
      CompetitionOverviewPageQuery,
      {
        slug,
      },
    );
  };
};
const useCompetitionOverviewPageQuery = () =>
  usePreloaded<CompetitionOverviewPageQueryType>();

export const lazyHandle = {
  titleI18n: defineMessage({ defaultMessage: "Overview" }),
};
